/**
 * Iter208.96 — Pagina pubblica di tracking pratica per il cliente finale.
 *
 * URL: `/pratica/:token`
 * Endpoint: `GET /api/public/tracking/:token`
 * Aggiornamento: polling ogni 30 secondi (silenzioso, senza spinner).
 * Non richiede login. Non espone dati sensibili oltre a servizio +
 * indirizzo + step del workflow.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  Circle,
  Clock,
  MapPin,
  Wrench,
  Calendar,
  CheckSquare,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;

function formatItDateTime(iso) {
  if (!iso) return "";
  try {
    const s = String(iso);
    // Il backend salva stringhe come "2026-04-05 10:00" o ISO 8601.
    // Normalizziamo entrambi in Date.
    const normalized = s.includes("T") ? s : s.replace(" ", "T");
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function StepRow({ step, isLast }) {
  const state = step.state; // "done" | "current" | "todo"
  const iconWrap =
    state === "done"
      ? "bg-emerald-500 text-white ring-4 ring-emerald-100"
      : state === "current"
      ? "bg-purple-600 text-white ring-4 ring-purple-200 animate-pulse"
      : "bg-white text-slate-400 border border-slate-300";
  const label =
    state === "done"
      ? "Completato"
      : state === "current"
      ? "In corso"
      : "Da fare";
  const labelClass =
    state === "done"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : state === "current"
      ? "text-purple-700 bg-purple-50 border-purple-200"
      : "text-slate-500 bg-slate-50 border-slate-200";
  const nameClass =
    state === "todo" ? "text-slate-500" : "text-slate-900 font-medium";
  return (
    <li
      className="relative flex items-start gap-4 pb-6"
      data-testid={`tracking-step-${step.state}`}
    >
      {/* Linea verticale */}
      {!isLast && (
        <span
          aria-hidden
          className="absolute left-[19px] top-10 bottom-0 w-0.5 bg-slate-200"
        />
      )}
      <div
        className={`relative z-10 shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconWrap}`}
      >
        {state === "done" ? (
          <CheckCircle size={22} weight="fill" />
        ) : state === "current" ? (
          <Clock size={20} weight="bold" />
        ) : (
          <Circle size={18} weight="regular" />
        )}
      </div>
      <div className="flex-1 pt-1">
        <div className={nameClass}>{step.name || "—"}</div>
        <span
          className={`inline-block mt-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${labelClass}`}
        >
          {label}
        </span>
      </div>
    </li>
  );
}

export default function ClientTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/api/public/tracking/${token}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError("not_found");
      } else {
        setError("network");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load(false);
  }, [load]);

  // Polling silenzioso ogni 30s finché la pratica non è consegnata o scaduta.
  useEffect(() => {
    if (!data || data.expired || data.delivered) return;
    timerRef.current = setInterval(() => load(true), POLL_MS);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [data, load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm" data-testid="tracking-loading">
          Caricamento…
        </div>
      </div>
    );
  }

  if (error === "not_found" || (data && data.expired)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div
          className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center"
          data-testid="tracking-unavailable"
        >
          <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Clock size={26} weight="regular" className="text-slate-500" />
          </div>
          <h1 className="text-lg font-semibold text-slate-800 mb-2">
            Questa pagina non è più disponibile
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Il link di tracking non è più attivo. Se hai bisogno di
            informazioni sulla tua pratica, rivolgiti al professionista
            che l'ha gestita.
          </p>
        </div>
      </div>
    );
  }

  if (error === "network" || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center">
          <p className="text-sm text-slate-600">
            Impossibile caricare le informazioni. Riprova tra qualche
            secondo.
          </p>
        </div>
      </div>
    );
  }

  const delivered = !!data.delivered;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Header */}
        <div
          className="rounded-2xl bg-white shadow-sm border overflow-hidden"
          data-testid="tracking-card"
        >
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 text-white">
            <div className="text-[11px] uppercase tracking-widest opacity-80">
              Housetag · Stato pratica
            </div>
            <div className="mt-2 flex items-start gap-2">
              <Wrench size={22} weight="bold" className="mt-0.5" />
              <h1
                className="text-xl font-semibold leading-snug"
                data-testid="tracking-service-name"
              >
                {data.service_name || "Servizio"}
              </h1>
            </div>
          </div>

          <div className="p-6 space-y-3">
            <div className="flex items-start gap-3 text-sm">
              <MapPin
                size={18}
                weight="fill"
                className="text-slate-400 mt-0.5 shrink-0"
              />
              <div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">
                  Immobile
                </div>
                <div
                  className="text-slate-800"
                  data-testid="tracking-property-address"
                >
                  {data.property_address || "—"}
                </div>
              </div>
            </div>

            {data.scheduled_appointment && (
              <div
                className="flex items-start gap-3 text-sm"
                data-testid="tracking-visit"
              >
                <Calendar
                  size={18}
                  weight="fill"
                  className="text-purple-500 mt-0.5 shrink-0"
                />
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">
                    Sopralluogo
                  </div>
                  <div className="text-slate-800">
                    {formatItDateTime(data.scheduled_appointment)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Corpo: steps oppure messaggio "consegnata" */}
          <div className="px-6 pb-8">
            {delivered ? (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex gap-3"
                data-testid="tracking-delivered"
              >
                <CheckSquare
                  size={26}
                  weight="fill"
                  className="text-emerald-600 shrink-0 mt-0.5"
                />
                <div>
                  <div className="font-semibold text-emerald-800">
                    Pratica consegnata
                  </div>
                  <p className="text-sm text-emerald-800/90 mt-1 leading-relaxed">
                    Chiedi al tuo professionista per la consegna dei
                    documenti.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-4">
                  Avanzamento
                </div>
                {data.steps && data.steps.length > 0 ? (
                  <ol
                    className="relative"
                    data-testid="tracking-steps-list"
                  >
                    {data.steps.map((s, i) => (
                      <StepRow
                        key={s.id}
                        step={s}
                        isLast={i === data.steps.length - 1}
                      />
                    ))}
                  </ol>
                ) : (
                  <div className="text-sm text-slate-500">
                    Il workflow verrà mostrato non appena il professionista
                    lo avvia.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-4">
          Questa pagina si aggiorna automaticamente ogni 30 secondi.
        </p>
      </div>
    </div>
  );
}
